import { useMemo } from 'react';
import { Button, Input, Popconfirm, Select, Tooltip, Typography } from 'antd';

import ToggleRow from '../common/ToggleRow';
import RelatedListFilter from './RelatedListFilter';
import RelatedListPreview from './RelatedListPreview';
import { destructivePopconfirmProps } from '../common/confirmDestructive';
import { optionSearchFilter } from '../Input';
import { CHEVRON_DOWN, CHEVRON_UP, TRASH } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { normalizeId } from '../../utils/ids';
import { combineSortOptions } from '../../hooks/useListSort';
import { LIST_NAME_MAX, isDraftId, sameReading, sortCandidates, sortElementOf, sortOf, viaCandidates } from '../../utils/relatedLists';

const { Text } = Typography;

/**
 * One of the card's OWN related lists, editable: its name, the card whose
 * items it shows, the lookup that links them (or any), its default filter, the
 * order it shows its items in (KPR-493: an element of the source card and a
 * direction, or the default order) and whether it is kept hidden. Moving it up
 * or down changes the item view's switcher order.
 *
 * It never decides anything the API decides: the lookups offered are those
 * the API will accept (they can hold an item of this card), and what the API
 * refuses is said under the field it names (`refusal`, `{ field, message }`).
 * Changing the source card clears the lookup and the filter, which name
 * elements of the card that was there before.
 */

const label = element => element?.label?.singular || element?.name || 'Lookup';

/** The order option for an element, `{ value: '<element id>:<direction>', label }` each way, worded as a list's sort menu words it. */
const orderOptionsFor = element => combineSortOptions([{
	value: element.id,
	label: element.label?.singular || element.name,
	dataType: element.dataType || 'text-small',
}]);

/** An order in words — "Step number (A–Z)", "Due (oldest)" — as the list's own sort menu says it. */
export function orderLabel(element, direction) {
	if (!element) return '';
	const options = orderOptionsFor(element);
	return (options.find(option => option.value.endsWith(`:${direction === 'desc' ? 'desc' : 'asc'}`)) || options[0]).label;
}

export default function RelatedListRow({
	list,
	index,
	total,
	sourceOptions,
	cardsById,
	getElementsForCard,
	line,
	noLink = false,
	collectionId,
	cardId,
	ready = true,
	saved = null,
	refusal = null,
	disabled = false,
	onChange,
	onMove,
	onRemove,
}) {
	// A new list wears a draft id until Save, so its key never changes.
	const key = list.id;
	const isDraft = isDraftId(list.id);
	const sourceId = normalizeId(list.sourceCardId);
	// Until the cards have loaded nothing can be called gone.
	const sourceKnown = !ready || !sourceId || sourceOptions.some(option => option.value === sourceId);
	const sourceChoices = sourceKnown ? sourceOptions : [{ value: sourceId, label: 'A card that is no longer available', searchText: '' }, ...sourceOptions];

	const viaElements = useMemo(
		() => viaCandidates({ sourceCardId: sourceId, line, getElementsForCard }),
		[sourceId, line, getElementsForCard]
	);
	const viaOptions = useMemo(() => {
		const options = viaElements.map(element => {
			const to = cardsById?.[normalizeId(element.options?.lookupCardId)];
			const target = to ? `looks up ${to.name}` : 'looks up any card';
			return {
				value: element.id,
				label: <span className='related-list__via-option'>{label(element)}<span className='related-list__via-target'>{target}</span></span>,
				searchText: `${label(element)} ${target}`,
				// The closed select shows the name alone; the target is for the list.
				short: label(element),
			};
		});
		// A lookup the list names that the source card no longer offers stays
		// shown, flagged, so the list is not silently changed by looking at it.
		if (ready && list.viaElementId && !options.some(option => option.value === list.viaElementId)) {
			options.unshift({
				value: list.viaElementId,
				label: <span className='related-list__via-option'>A lookup that no longer qualifies</span>,
				short: 'A lookup that no longer qualifies',
				searchText: 'no longer qualifies',
			});
		}
		return options;
	}, [viaElements, cardsById, list.viaElementId, ready]);
	const sourceDef = sourceId ? cardsById?.[sourceId] : null;
	const viaLost = ready && Boolean(sourceDef) && Boolean(list.viaElementId) && !viaElements.some(element => element.id === list.viaElementId);

	// THE ORDER (KPR-493): one select, element and direction together, as a
	// list's own sort menu offers them. Cleared, the list shows the default order.
	const order = sortOf(list);
	const orderElements = useMemo(
		() => sortCandidates({ sourceCardId: sourceId, getElementsForCard }),
		[sourceId, getElementsForCard]
	);
	const orderElement = order ? sortElementOf(orderElements, order.element) : null;
	const orderOptions = useMemo(() => {
		const options = orderElements.flatMap(element => orderOptionsFor(element));
		// An order the source card no longer offers stays shown, flagged, so
		// the list is not silently changed by looking at it.
		if (ready && order && !orderElement) {
			options.unshift({ value: `${order.element}:${order.direction}`, label: 'An element that can no longer order this list' });
		}
		return options;
	}, [orderElements, order, orderElement, ready]);
	// A copy of the element (its lineage) stands for it: shown as the copy.
	const orderValue = order ? `${orderElement ? orderElement.id : order.element}:${order.direction}` : undefined;
	const orderLost = ready && Boolean(sourceDef) && Boolean(order) && !orderElement;

	const readingKey = saved && !isDraft && sameReading(list, saved)
		? `${sourceId}|${list.viaElementId || ''}|${list.filter || ''}`
		: null;

	const name = list.name?.trim() || 'New list';
	const nameError = refusal?.field === 'name' ? refusal.message : '';
	const sourceError = refusal?.field === 'sourceCardId' ? refusal.message : '';
	const viaError = refusal?.field === 'viaElementId' ? refusal.message : '';
	const filterError = refusal?.field === 'filter' ? refusal.message : '';
	const sortError = refusal?.field === 'sort' ? refusal.message : '';
	// A refusal that names no field of its own is the list's.
	const wholeError = refusal && !['name', 'sourceCardId', 'viaElementId', 'filter', 'sort'].includes(refusal.field) ? refusal.message : '';

	const removeButton = (
		<Button
			type='text'
			size='small'
			danger
			icon={<TRASH />}
			aria-label={`Remove ${name}`}
			disabled={disabled}
			onClick={isDraft ? onRemove : undefined}
		/>
	);

	return (
		<li className='related-list' data-testid='related-list-row'>
			<div role='group' aria-label={name} className='related-list__body'>
			<div className='related-list__head'>
				<div className='related-list__name'>
					<label htmlFor={`related-list-${key}-name`} className='d-block'>Name</label>
					<Input
						{...NO_TEXT_ASSIST}
						id={`related-list-${key}-name`}
						size='large'
						value={list.name}
						maxLength={LIST_NAME_MAX}
						placeholder='Open work items'
						status={nameError ? 'error' : undefined}
						disabled={disabled}
						onChange={event => onChange({ name: event.target.value })}
					/>
					{nameError ? <Text type='danger' className='related-list__error d-block'>{nameError}</Text> : null}
				</div>
				<div className='related-list__actions'>
					<Tooltip title='Move up'>
						<Button type='text' size='small' icon={<CHEVRON_UP />} aria-label={`Move ${name} up`} disabled={disabled || index === 0} onClick={() => onMove(-1)} />
					</Tooltip>
					<Tooltip title='Move down'>
						<Button type='text' size='small' icon={<CHEVRON_DOWN />} aria-label={`Move ${name} down`} disabled={disabled || index === total - 1} onClick={() => onMove(1)} />
					</Tooltip>
					<Tooltip title='Remove'>
						{!isDraft ? (
							<Popconfirm
								title={`Remove “${name}”?`}
								description='It stops showing on this card’s items when you save. Cancel the card’s changes to bring it back.'
								onConfirm={onRemove}
								disabled={disabled}
								{...destructivePopconfirmProps('Remove')}
							>
								{removeButton}
							</Popconfirm>
						) : removeButton}
					</Tooltip>
				</div>
			</div>

			<div className='related-list__fields'>
				<div>
					<label htmlFor={`related-list-${key}-source`} className='d-block'>Items of</label>
					<Select
						id={`related-list-${key}-source`}
						size='large'
						className='w-100'
						showSearch
						value={sourceId || undefined}
						options={sourceChoices}
						filterOption={optionSearchFilter}
						placeholder='Choose a card'
						status={sourceError || !sourceKnown ? 'error' : undefined}
						disabled={disabled}
						onChange={next => onChange({ sourceCardId: next || '', viaElementId: null, filter: null, sort: null })}
					/>
					{sourceError ? <Text type='danger' className='related-list__error d-block'>{sourceError}</Text> : null}
					{!sourceKnown ? (
						<Text type='danger' className='related-list__error d-block'>
							This card is no longer available here. Choose another, or remove the list.
						</Text>
					) : null}
				</div>

				<div>
					<label htmlFor={`related-list-${key}-via`} className='d-block'>Linked through</label>
					<Select
						id={`related-list-${key}-via`}
						size='large'
						className='w-100'
						showSearch
						allowClear
						value={list.viaElementId || undefined}
						options={viaOptions}
						optionLabelProp='short'
						filterOption={optionSearchFilter}
						placeholder='Any lookup that points here'
						status={viaError || viaLost ? 'error' : undefined}
						disabled={disabled || !sourceId}
						onChange={next => onChange({ viaElementId: next || null })}
					/>
					{viaError ? <Text type='danger' className='related-list__error d-block'>{viaError}</Text> : null}
					{viaLost && !viaError ? (
						<Text type='danger' className='related-list__error d-block'>
							This lookup was removed, or no longer points at this card. Choose another.
						</Text>
					) : null}
					{!viaError && !viaLost && sourceId && !list.viaElementId && noLink ? (
						<Text type='warning' className='related-list__error d-block'>
							No lookup on {sourceDef?.name || 'that card'} points at this card, so the list would be empty. Add one to it first.
						</Text>
					) : null}
					{sourceId && !viaError && !viaLost && !(noLink && !list.viaElementId) ? (
						<Text type='secondary' className='related-list__hint d-block'>
							{list.viaElementId
								? 'Lists the items whose lookup holds the item being viewed.'
								: 'Left empty, any lookup on the card that points at this card counts.'}
						</Text>
					) : null}
				</div>
			</div>

			<div className='related-list__filter-block'>
				<span className='related-list__label'>Default filter</span>
				<RelatedListFilter
					sourceCardId={sourceId}
					filter={list.filter || ''}
					onChange={next => onChange({ filter: next })}
					cardsById={cardsById}
					getElementsForCard={getElementsForCard}
					collectionId={collectionId}
					disabled={disabled}
				/>
				{filterError ? <Text type='danger' className='related-list__error d-block'>{filterError}</Text> : null}
			</div>

			<div className='related-list__order'>
				<label htmlFor={`related-list-${key}-order`} className='d-block'>Order</label>
				<Select
					id={`related-list-${key}-order`}
					size='large'
					className='w-100'
					showSearch
					allowClear
					value={orderValue}
					options={orderOptions}
					filterOption={optionSearchFilter}
					placeholder='Default order (primary date, newest first)'
					status={sortError || orderLost ? 'error' : undefined}
					disabled={disabled || !sourceId}
					onChange={(next) => {
						if (!next) { onChange({ sort: null }); return; }
						const at = next.lastIndexOf(':');
						onChange({ sort: { element: next.slice(0, at), direction: next.slice(at + 1) } });
					}}
				/>
				{sortError ? <Text type='danger' className='related-list__error d-block'>{sortError}</Text> : null}
				{orderLost && !sortError ? (
					<Text type='danger' className='related-list__error d-block'>
						The element this list was ordered by was removed or can no longer order it, so it shows in the default order. Choose another, or clear it.
					</Text>
				) : null}
				{sourceId && !sortError && !orderLost ? (
					<Text type='secondary' className='related-list__hint d-block'>
						{order
							? 'Items that tie keep the default order. A viewer can still sort the list another way.'
							: 'Left empty, the list shows its items in the default order: primary date, newest first.'}
					</Text>
				) : null}
			</div>

			<ToggleRow
				label='Hidden'
				description='Kept, but not shown on the item view.'
				checked={list.hidden === true}
				onChange={checked => onChange({ hidden: checked })}
				disabled={disabled}
				name={`related-list-${key}-hidden`}
				aria-label={`Hide ${name}`}
				className='related-list__hidden'
			/>

			{wholeError ? <Text type='danger' className='related-list__error d-block'>{wholeError}</Text> : null}

			<RelatedListPreview
				listId={isDraft ? '' : list.id}
				listName={list.name?.trim()}
				readingKey={readingKey}
				collectionId={collectionId}
				cardId={cardId}
			/>
			</div>
		</li>
	);
}
